/**
 * Wall clock in a named zone → instant, for booking a trial or appointment ("Tuesday 18:30 at the gym").
 * Only used to create an instant; plan validity and every other business date stays a `YYYY-MM-DD` string (CLAUDE.md rule 3).
 */
function offsetMinutes(epochMs: number, timeZone: string): number {
  const p = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(epochMs);
  const n = (t: string) => Number(p.find((x) => x.type === t)?.value ?? 0);
  const asUtc = Date.UTC(n("year"), n("month") - 1, n("day"), n("hour"), n("minute"), n("second"));
  return Math.round((asUtc - Math.floor(epochMs / 1000) * 1000) / 60_000);
}

/** `date` is `YYYY-MM-DD`, `hhmm` is `HH:MM`, both on the wall clock of `timeZone`. */
export function zonedToInstant(date: string, hhmm: string, timeZone: string): number {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const [hh, mm] = hhmm.split(":").map(Number) as [number, number];
  const naive = Date.UTC(y, m - 1, d, hh, mm);
  let guess = naive - offsetMinutes(naive, timeZone) * 60_000;
  // Across a DST edge the first guess can be an hour off: one correction settles it.
  guess = naive - offsetMinutes(guess, timeZone) * 60_000;
  return guess;
}

/** `HH:MM` on the zone's wall clock. */
export function hhmmOf(epochMs: number, timeZone: string): string {
  const p = new Intl.DateTimeFormat("en-GB", { timeZone, hourCycle: "h23", hour: "2-digit", minute: "2-digit" }).formatToParts(epochMs);
  return `${p.find((x) => x.type === "hour")?.value}:${p.find((x) => x.type === "minute")?.value}`;
}

export const addMinutesHHMM = (hhmm: string, minutes: number): string => {
  const [h, m] = hhmm.split(":").map(Number) as [number, number];
  const t = Math.min(23 * 60 + 59, h * 60 + m + minutes);
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};
